import React from 'react';
import PropTypes from 'prop-types';
import { Text, Button } from '@reservamos/elements';
import { Trans, useTranslation } from 'react-i18next';
import tapatioToursBus from 'images/icons/tapatio-tours-bus.png';
import tapatioTourMobile from 'images/icons/tapatio-tour-mobile.png';
import openUrlInBackground from 'utils/openUrlInBackground';

const LOCATION_KEYS = [
  'centro_historico',
  'tlaquepaque',
  'zapopan',
  'tonala',
  'tequila',
  'chapala_ajijic',
];

const TAG_CLASS_NAME =
  'rounded-full bg-[#eef2f7] px-2 py-0.5 font-semibold leading-tight text-[#0a3d6e] text-xs';

/**
 * TapatioBanner component.
 * Renders a promotional card inviting users to discover Tapatio Tour panoramic bus routes.
 * @param {object} props - href, onClick.
 * @param {string} props.href - URL opened when the CTA is clicked.
 * @param {Function} [props.onClick] - Optional click handler (e.g. analytics tracking).
 */
const TapatioBanner = ({ href, onClick }) => {
  const { t } = useTranslation('purchase');

  if (!href) {
    return null;
  }

  /** Opens Tapatio Tour URL in a background tab after optional tracking. */
  const handleClick = () => {
    if (!href) return;

    try {
      onClick?.();
    } catch (_) {
      // Ignore tracking errors so navigation always proceeds.
    }

    openUrlInBackground(href);
  };

  const locationTags = (
    <div className="flex flex-wrap gap-1">
      {LOCATION_KEYS.map((locationKey) => (
        <span key={locationKey} className={TAG_CLASS_NAME}>
          {t(`tapatio_banner.locations.${locationKey}`)}
        </span>
      ))}
    </div>
  );

  const ctaButton = (
    <Button
      isRounded
      variant="noFill"
      colorActiveSelection="primary"
      fontSize="XS"
      fontWeight="semibold"
      hideShadow
      onClick={handleClick}
      text={t('tapatio_banner.cta')}
      padding="M"
      mobilePadding="M"
    />
  );

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md md:hidden">
        <div className="relative">
          <img
            src={tapatioTourMobile}
            alt=""
            aria-hidden="true"
            className="block w-full object-cover object-left"
          />
          <div className="absolute inset-0 flex items-center px-4">
            <div className="max-w-[58%]">
              <Text color="white" size="S" mobileSize="S">
                <Trans>{t('tapatio_banner.description')}</Trans>
              </Text>
            </div>
          </div>
        </div>

        <div className="px-2 py-2">
          {locationTags}
          <div className="mt-3">{ctaButton}</div>
        </div>
      </div>

      <div className="hidden overflow-hidden rounded-lg shadow-md md:flex md:flex-row md:justify-between">
        <img
          src={tapatioToursBus}
          alt=""
          aria-hidden="true"
          className="h-full w-44 flex-shrink-0 object-cover md:w-52"
        />

        <div className="flex flex-col px-2 py-1 md:flex-row md:items-center">
          <div className="mr-3 flex flex-1 flex-col justify-center">
            <Text size="S" mobileSize="XS">
              <Trans>{t('tapatio_banner.description')}</Trans>
            </Text>

            <div className="mt-1">{locationTags}</div>
          </div>

          {ctaButton}
        </div>
      </div>
    </>
  );
};

TapatioBanner.propTypes = {
  href: PropTypes.string,
  onClick: PropTypes.func,
};

TapatioBanner.defaultProps = {
  href: '',
  onClick: null,
};

export default TapatioBanner;
